import { useState, useEffect, useCallback } from 'react';
import { LoginBox } from './LoginBox';
import { Search } from './Search';
import { IndividualDetail } from './IndividualDetail';
import { Header } from './Header';
import { Descendants, Ancestors } from './Descendants';
import { Database } from './Data';
import { useHashRouter } from './hooks/useHashRouter';
import styles from './App.module.css';
import './index.css';

const database = new Database();

function App() {
    const { path, chunks, navigate } = useHashRouter();
    const [error, setError] = useState<string>('');
    const [loginInProgress, setLoginInProgress] = useState<boolean>(false);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

    const login = useCallback(async (username: string, password: string) => {
        console.log('login u:' + username + ' p:' + password);
        setLoginInProgress(true);
        setError('');

        try {
            await database.connect(username, password);
            localStorage.setItem('username', username);
            localStorage.setItem('password', password);
            setIsLoggedIn(true);
            navigate('Individuals', 'individuals');
        } catch (e) {
            const message = e instanceof Error ? e.message : 'Unknown error';
            setError('Login failed: ' + message);
            localStorage.clear();
        } finally {
            setLoginInProgress(false);
        }
    }, [navigate]);

    const logout = useCallback(() => {
        console.log('logout');
        localStorage.clear();
        setIsLoggedIn(false);
        navigate('Family Tree: Login', 'login');
    }, [navigate]);

    const search = useCallback(() => {
        navigate('Individuals', 'individuals');
    }, [navigate]);

    const showDetails = useCallback((individualId: number) => {
        navigate('Individual detail', 'individuals/' + individualId);
    }, [navigate]);

    const showAncestors = useCallback((individualId: number) => {
        navigate(
            'Individual ancestors',
            'individuals/' + individualId + '/ancestors',
        );
    }, [navigate]);

    const showDescendants = useCallback((individualId: number) => {
        navigate(
            'Individual descendants',
            'individuals/' + individualId + '/descendants',
        );
    }, [navigate]);

    // Auto-login on mount
    useEffect(() => {
        const connect = async () => {
            console.log('App.connect()');
            const username = localStorage.getItem('username');
            const password = localStorage.getItem('password');
            if (!username || !password) {
                navigate('Family Tree', 'login');
                return;
            }
            login(username, password);
        };
        connect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Redirect to login if accessing protected route while not logged in
    useEffect(() => {
        if (!isLoggedIn && path !== 'login' && !loginInProgress) {
            navigate('Family Tree: Login', 'login');
        }
    }, [isLoggedIn, path, loginInProgress, navigate]);

    const header = (
        <Header
            path={path}
            logout={logout}
            search={search}
        />
    );

    console.log('App.render ' + path);

    // URL: /login
    if (path === 'login') {
        if (loginInProgress) {
            return (
                <div className={styles.app}>
                    {header}
                    <div className={styles.loggingIn}>Logging in...</div>
                </div>
            );
        }
        return (
            <div className={styles.app}>
                {header}
                <LoginBox login={login} error={error} />
            </div>
        );
    }

    // Protected routes require login - useEffect handles redirect
    if (!isLoggedIn) {
        return null;
    }

    // URL: individuals
    if (path === 'individuals') {
        return (
            <div className={styles.app}>
                {header}
                <Search
                    database={database}
                    showDetails={showDetails}
                />
            </div>
        );
    }

    if (chunks.length >= 2 && chunks[0] === 'individuals') {
        const id = parseInt(chunks[1]);
        if (isNaN(id)) {
            return (
                <div className={styles.app}>
                    {header}
                    <div className={styles.invalidPath}>Invalid individual id</div>
                </div>
            );
        }
        if (chunks.length === 2) {
            // URL: individuals/$id
            const individual = database.individual(id);
            if (!individual) {
                return (
                    <div className={styles.app}>
                        {header}
                        <div className={styles.invalidPath}>Invalid individual ID {id}</div>
                    </div>
                );
            }
            return (
                <div className={styles.app}>
                    {header}
                    <IndividualDetail
                        individual={individual}
                        showDetails={showDetails}
                        showDescendants={showDescendants}
                        showAncestors={showAncestors}
                    />
                </div>
            );
        }

        if (chunks.length === 3) {
            const individual = database.individual(id);
            if (!individual) {
                return (
                    <div className={styles.app}>
                        {header}
                        <div className={styles.invalidPath}>Invalid individual ID {id}</div>
                    </div>
                );
            }
            if (chunks[2] === 'descendants') {
                // URL: individuals/$id/descendants
                return (
                    <div className={styles.app}>
                        {header}
                        <Descendants
                            individual={individual}
                            showDetails={showDetails}
                        />
                    </div>
                );
            }
            if (chunks[2] === 'ancestors') {
                // URL: individuals/$id/ancestors
                return (
                    <div className={styles.app}>
                        {header}
                        <Ancestors
                            individual={individual}
                            showDetails={showDetails}
                        />
                    </div>
                );
            }
        }
    }

    // Not a valid route, error!
    return (
        <div className={styles.app}>
            {header}
            <div className={styles.invalidPath}>Invalid path</div>
        </div>
    );
}

export default App;
